import React, { useEffect, useMemo, useState } from 'react';
import { Box, Divider, Grid } from '@mui/material';
import { useOrderDetails } from 'components/context/order_details';
import {
  buildProgressSearchParamOverrides,
  onCheckoutPageView,
  onCheckoutPageViewV2,
} from 'components/pages/checkout/utilities';
import {
  eligibleOrderPromotions,
  getTopPromoEligibility,
} from 'components/pages/product/helpers';
import ActionableCart from 'components/common/receipt/actionable_cart';
import CartBanner, { CART_BANNER_CONFIGS } from 'components/common/cart_banner';
import CartHeader from 'components/common/cart_header';
import CartReceipt from 'components/common/receipt/cart_receipt';
import CheckoutButton from 'components/common/checkout_button';
import OutOfStockBanner from 'components/common/out_of_stock_banner';
import ProductPromotionBanner from 'components/common/product_promotion_banner';
import ShippingBanner from 'components/common/shipping_banner';
import PageLoader from 'components/common/page_loader';
import CartClient from 'clients/cart';
import CheckoutClient from 'clients/checkout';
import Toast from 'components/common/toast';
import RecommendedProductsModule from 'components/common/recommended_products_module';
import ProductNotificationBanner from 'components/common/product_notification_banner';
import { INELIGIBILITY_REASONS } from 'constants/financing';
import { FOOTNOTE_SYMBOL } from 'components/common/banners/banner_helpers';
import { useFeatureFlags } from 'components/context/feature_flags';
import { COMPONENT_NAMES } from 'constants/tracking';
import useIdv, {
  checkingIdvLink,
  goldenPathIdvLink,
  idvLink,
  idvLoginLink,
  removeIdvSearchParams,
} from 'components/pages/cart/hooks/use_idv';
import { updateCartCount } from 'utilities/nav';
import DashboardModeFooter from 'components/common/dashboard_mode_footer';
import {
  IDV_STATUS,
  buildDebitCardHelpLink,
  buildSignUpTroubleshootingLink,
  buildCheckoutSignInLink,
  removeCheckoutLoginRedirectParam,
} from 'utilities/links';
import {
  isDashboardMode,
  checkRedirectedFromCheckoutLogin,
  isSourceQsrHardwareFlow,
} from 'helpers/search_parameter_helpers';
import { localizedShopPath } from 'utilities/locales';
import { shopFooterDisclaimers } from 'utilities/disclaimers';
import {
  isSoftwareWithFreeHardware,
  sortProductByHardwareOrSoftware,
  anySoftwareBlockingCheckout,
  anySoftwareBlockingCheckoutLoginRedirect,
} from 'utilities/software';

const Cart = () => {
  const { checkingVyiFlowEnabled, recommendedProductsEnabled } =
    useFeatureFlags();
  const { number, language, store, trackingData } = useOrderDetails();
  const [toast, setToast] = useState({});
  const [orderData, setOrderData] = useState(null);
  const [financeData, setFinanceData] = useState(null);
  const [financingIneligibilityReasons, setFinancingIneligibilityReasons] =
    useState([]);
  const [logisticsData, setLogisticsData] = useState(null);
  const [refreshingData, setRefreshingData] = useState(false);
  const [promoEligibility, setPromoEligibility] = useState({});
  const [blockCheckout, setBlockCheckout] = useState(true);
  const [redirectBackToCartLoading, setRedirectBackToCartLoading] =
    useState(false);
  const {
    idvNeeded,
    idvFailed,
    showVerificationSuccessBanner,
    fetchIdvStatus,
    updateIdvStatus,
    updateShowVerificationSuccessBanner,
  } = useIdv();

  const cartClient = useMemo(() => new CartClient(number, store, language), []);

  const checkoutClient = useMemo(() => {
    return new CheckoutClient(number, store, language);
  }, []);

  const financingIneligibleItem = useMemo(() => {
    if (
      !orderData ||
      orderData.upfront_card_payment_only ||
      !financingIneligibilityReasons.includes(
        INELIGIBILITY_REASONS.INELIGIBLE_PRODUCTS,
      )
    ) {
      return null;
    }

    const ineligibleCartItem = orderData.cart_summary.find(
      ({ properties: { financing_ineligible } }) => financing_ineligible,
    );

    return ineligibleCartItem || null;
  }, [orderData, financingIneligibilityReasons]);

  const checkingItem = useMemo(() => {
    if (!orderData) {
      return null;
    }

    const checkingItem = orderData.cart_summary.find(
      ({ is_checking_product }) => is_checking_product,
    );

    return checkingItem || null;
  }, [orderData]);

  const ineligibleItem = useMemo(() => {
    if (!orderData) return null;

    // find any cart item that is ineligible
    const ineligibleCartItem = orderData.cart_summary.find(
      ({ user_eligible }) => !user_eligible,
    );

    return ineligibleCartItem;
  }, [orderData]);

  const software = useMemo(() => {
    if (!orderData) return [];

    const { software } = sortProductByHardwareOrSoftware(
      orderData.cart_summary,
    );

    return software;
  }, [orderData]);

  /**
   * Compute if checkout needs to be blocked based on the cart data
   * based on various conditions
   * @param {*} data - cart data
   * @returns {boolean} true if checkout needs to be blocked
   */
  const computeBlockCheckoutState = (data) => {
    const {
      cart_summary: cartSummary,
      checking: {
        idv_required: checkingIdvRequired,
        ineligible_reason: checkingIneligibleReason,
      },
      items_count: itemsCount,
      idv: {
        required: idvRequired,
        status: idvStatus,
        block_suspended: idvBlockSuspended,
      },
    } = data;

    const isOutOfStockOrNotEligible = cartSummary.some(
      ({ out_of_stock, user_eligible }) => out_of_stock || !user_eligible,
    );

    const isEmptyCart = itemsCount === 0;

    const isIdvSuspended = idvRequired && idvStatus === IDV_STATUS.SUSPENDED;

    const isIdvBlocked =
      idvBlockSuspended && idvStatus === IDV_STATUS.SUSPENDED;

    const isZeroTotalWithSoftware = isSoftwareWithFreeHardware(data);

    const isAlreadySubscribedToSoftware = anySoftwareBlockingCheckout(data);

    const isCheckingBlocking = checkingIdvRequired && checkingIneligibleReason;

    return (
      isOutOfStockOrNotEligible ||
      isEmptyCart ||
      isIdvSuspended ||
      isIdvBlocked ||
      isZeroTotalWithSoftware ||
      isAlreadySubscribedToSoftware ||
      isCheckingBlocking
    );
  };

  const getLoggedInIdvLink = (data) => {
    let link = idvLink;

    if (isSourceQsrHardwareFlow()) {
      link = goldenPathIdvLink;
    } else if (
      data.checking?.idv_required &&
      !data.checking?.ineligible_reason &&
      checkingVyiFlowEnabled
    ) {
      link = checkingIdvLink;
    }

    return link;
  };

  // after user logs in from a cart login redirect, we need to check
  // if they should either stay on the cart page or get auto redirected to checkout
  // eslint-disable-next-line complexity
  const handleCheckoutLoginRedirect = (data) => {
    if (!data) return;

    const currentUserLoggedIn = data.current_user?.is_logged_in;

    const {
      redirectedFromIdv,
      redirectedFromIdvLogin,
      redirectedFromCheckoutLogin,
    } = checkRedirectedFromCheckoutLogin();

    const redirectedBackToCart =
      redirectedFromIdv ||
      redirectedFromIdvLogin ||
      redirectedFromCheckoutLogin;

    // skip the redirect to check if we didn't come from a known redirect
    if (!redirectedBackToCart || !currentUserLoggedIn) return;

    const { isIdvFailed, isIdvNeeded } = fetchIdvStatus(data);

    // if we've been redirected from a login, show the loading spinner
    setRedirectBackToCartLoading(true);

    // if we came from idv login, and idv is needed, redirect them to checking idv or idv
    if (redirectedFromIdvLogin && isIdvNeeded) {
      window.location.href = getLoggedInIdvLink(data);
      return;
    }

    // if we aren't redirecting to idv, remove any extra login params from the URL
    let urlSearchParams = new URLSearchParams(window.location.search);
    if (redirectedFromIdv || redirectedFromIdvLogin) {
      urlSearchParams = removeIdvSearchParams(urlSearchParams);
    }

    if (redirectedFromCheckoutLogin) {
      urlSearchParams = removeCheckoutLoginRedirectParam(urlSearchParams);
    }

    urlSearchParams = urlSearchParams.toString();

    // check if we need to block checkout redirect due to idv, software, checking, or success banner
    const isSoftwareBlockingCheckout =
      anySoftwareBlockingCheckoutLoginRedirect(data);
    const isCheckingBlockingCheckout =
      data.checking?.idv_required && data.checking?.ineligible_reason;
    const isVerificationBannerBlockingCheckout = redirectedFromIdv
      ? updateShowVerificationSuccessBanner(data)
      : false;

    const preventRedirectToCheckout =
      isIdvNeeded ||
      isIdvFailed ||
      isSoftwareBlockingCheckout ||
      isCheckingBlockingCheckout ||
      isVerificationBannerBlockingCheckout;

    if (preventRedirectToCheckout) {
      // hide loading spinner if we aren't redirecting to checkout
      setRedirectBackToCartLoading(false);
      // update the URL to remove any login params to prevent further redirects
      const cartUrlWithoutSearch = `${window.location.href.split('?')[0]}`;
      const url =
        urlSearchParams === ''
          ? cartUrlWithoutSearch
          : `${cartUrlWithoutSearch}?${urlSearchParams}`;
      window.history.replaceState(null, null, url);
    } else {
      // if we pass all above checks, we can redirect them to checkout post login
      const checkoutLink = data.checkout_v3_enabled
        ? `${localizedShopPath()}/checkout/v3/delivery`
        : `${localizedShopPath()}/checkout/v2/information`;
      window.location.href =
        urlSearchParams === ''
          ? checkoutLink
          : `${checkoutLink}?${urlSearchParams}`;
    }
  };

  const orderRefresh = () => {
    setRefreshingData(true);
    return checkoutClient
      .orderSummary()
      .then((data) => {
        handleCheckoutLoginRedirect(data);
        setOrderData(data);
        setBlockCheckout(computeBlockCheckoutState(data));
        updateIdvStatus(data);
        updateCartCount(data.items_count); // keep cart icon up to date
      })
      .finally(() => setRefreshingData(false));
  };

  const shippingLogistics = () =>
    orderData &&
    cartClient.shippingLogistics().then(
      (data) => setLogisticsData(data),
      () => {},
    );

  const previewFinance = () =>
    orderData &&
    checkoutClient.previewOffer().then(
      (data) => {
        setFinancingIneligibilityReasons([]);
        setFinanceData(data);
      },
      (reason) => {
        // `reason` may or may not be a promise, resolving it allows us to work with it regardless
        Promise.resolve(reason).then((error) => {
          setFinancingIneligibilityReasons(error.ineligibility_reasons ?? []);
        });
        setFinanceData(null);
      },
    );

  const promotionEligibility = () => {
    if (!orderData) return;

    const promosByPriority = eligibleOrderPromotions(orderData);
    getTopPromoEligibility(promosByPriority, cartClient, (eligibility) => {
      eligibility && setPromoEligibility(eligibility);
    });
  };

  const getCheckoutLink = () => {
    // Set any additional modified shop params here
    const finalUrlParams = new URLSearchParams();
    const defaultPath = orderData.checkout_v3_enabled
      ? 'checkout/v3/delivery'
      : 'checkout/v2/information';
    const currentUserLoggedIn = orderData.current_user?.is_logged_in;

    if (idvNeeded) {
      if (currentUserLoggedIn) {
        return getLoggedInIdvLink(orderData);
      } else {
        return idvLoginLink;
      }
    }

    if (software.length > 0 && !currentUserLoggedIn) {
      return buildCheckoutSignInLink({
        signUpOnly: !idvNeeded,
      });
    }

    if (isDashboardMode()) {
      const progressSearchParamsOverridesHash =
        buildProgressSearchParamOverrides('/information');

      for (const [key, value] of Object.entries(
        progressSearchParamsOverridesHash,
      )) {
        finalUrlParams.append(key, value);
      }
    }

    return finalUrlParams.toString()
      ? `${defaultPath}?${finalUrlParams.toString()}`
      : defaultPath;
  };

  useEffect(() => {
    orderRefresh();
    onCheckoutPageView('Review Cart', trackingData);
    onCheckoutPageViewV2({
      componentName: COMPONENT_NAMES.APPS.CART.YOUR_CART,
      additionalParameters: trackingData,
    });
  }, []);

  useEffect(() => {
    previewFinance();
    promotionEligibility();
    shippingLogistics();
  }, [orderData]);

  const renderCartBanners = () => {
    if (!orderData) return null;

    return (
      <>
        <ProductNotificationBanner cart={orderData.cart_summary} />
        <ProductPromotionBanner {...promoEligibility} />
        <CartBanner
          config={CART_BANNER_CONFIGS[logisticsData?.logistics_message]}
        />
        <ShippingBanner shipQuotes={logisticsData?.extended_shipping} />
        <OutOfStockBanner cart={orderData.cart_summary} />
        {ineligibleItem && (
          <CartBanner
            config={CART_BANNER_CONFIGS.INELIGIBLE_ITEM}
            footnote={FOOTNOTE_SYMBOL.cross}
            name={ineligibleItem.product_variant.title}
          />
        )}
        {financingIneligibleItem && (
          <CartBanner
            config={CART_BANNER_CONFIGS.FINANCING_INELIGIBLE_ITEM}
            name={financingIneligibleItem.product_variant.title}
          />
        )}
        {idvFailed && (
          <CartBanner
            config={CART_BANNER_CONFIGS.IDV_FAILED}
            helpUrl={buildSignUpTroubleshootingLink()}
          />
        )}
        {idvNeeded &&
          (orderData && orderData.current_user?.is_logged_in ? (
            <CartBanner
              config={CART_BANNER_CONFIGS.IDV_REQUIRED_LOGGED_IN}
              idvUrl={getLoggedInIdvLink(orderData)}
            />
          ) : (
            <CartBanner
              config={CART_BANNER_CONFIGS.IDV_REQUIRED_GUEST}
              idvUrl={idvLoginLink}
            />
          ))}
        {showVerificationSuccessBanner && (
          <CartBanner
            config={CART_BANNER_CONFIGS.IDV_SUCCESS}
            helpUrl={buildSignUpTroubleshootingLink()}
          />
        )}
        {orderData.checking?.idv_required &&
          orderData.checking?.ineligible_reason &&
          checkingItem && (
            <CartBanner
              config={
                CART_BANNER_CONFIGS[
                  `CHECKING_${orderData.checking.ineligible_reason.toUpperCase()}`
                ]
              }
              url={buildDebitCardHelpLink()}
              productName={checkingItem.product_variant.title}
            />
          )}
        {isSoftwareWithFreeHardware(orderData) && (
          <CartBanner config={CART_BANNER_CONFIGS.SOFTWARE_WITHOUT_HARDWARE} />
        )}
      </>
    );
  };

  const handleToastClose = () => {
    setToast({ open: false });
  };

  if (!orderData || redirectBackToCartLoading) {
    return <PageLoader delayMs={500} />;
  }

  return (
    <>
      <Toast {...toast} onClose={handleToastClose} />
      <Box sx={{ py: 4, px: { xs: '5%', lg: '15%' } }}>
        <Grid container columnSpacing={6} rowSpacing={2} mb={2}>
          <Grid item xs={12} md={8}>
            <CartHeader
              itemsCount={orderData.items_count}
              total={orderData.total.amount}
              currency={orderData.total.currency}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <CheckoutButton
              id="checkout-btn"
              disabled={refreshingData || blockCheckout}
              href={getCheckoutLink()}
              passExistingParams={!idvNeeded}
              trackingData={trackingData}
            />
          </Grid>
        </Grid>
        <Grid container columnSpacing={6} rowSpacing={2}>
          <Grid item xs={12} md={8}>
            <Divider />

            {renderCartBanners()}

            <ActionableCart
              orderData={orderData}
              shipQuotes={logisticsData?.extended_shipping}
              cartClient={cartClient}
              orderRefresh={orderRefresh}
              blockCheckout={blockCheckout}
              setBlockCheckout={setBlockCheckout}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <CartReceipt
              loading={refreshingData}
              orderData={orderData}
              financeData={financeData}
              cartClient={cartClient}
              orderRefresh={orderRefresh}
            />
          </Grid>
          <Grid item xs={12} sx={{ display: { md: 'none' } }}>
            <CheckoutButton
              id="mobile-checkout-btn"
              disabled={refreshingData || blockCheckout}
              href={getCheckoutLink()}
              passExistingParams={!idvNeeded}
              trackingData={trackingData}
            />
          </Grid>
        </Grid>

        {recommendedProductsEnabled && (
          <Box
            sx={{
              mt: 5,
            }}
          >
            <RecommendedProductsModule
              cartTrackingData={trackingData}
              orderData={orderData}
              setToast={setToast}
              orderRefresh={orderRefresh}
            />
          </Box>
        )}

        {isDashboardMode() && (
          <DashboardModeFooter disclaimers={shopFooterDisclaimers()} />
        )}
      </Box>
    </>
  );
};

export default Cart;
